import { ConfigurationProfilesView } from '../view/ConfigurationProfilesView';
import { templateConfigurationProfiles } from '../view/template';
import { ConfigurationProfilesRepository } from '../repository/ConfigurationProfilesRepository';
import { BaseWebComponentController, WebComponentEvent } from '../../../framework/BaseWebComponentController';
import { ConfigurationProfilesWebComponent } from '..';
import { ConfigurationProfilesWebComponentSchema } from '../types';
import { GlobalExclusionsEvents } from '../../exclusionsPanel/types';
import { showErrorToast, showSuccessToast } from '../../../ui/gzToastNotification';
import { NavigationConstants } from '@nirvana/shared/navigation';
import { EditPolicyAssignmentPanelWebComponent } from '../../editPolicyAssignmentPanel';
import { EditPolicyAssignmentEvents } from '../../editPolicyAssignmentPanel/types';
import { translateFn } from '../../../utils/translator.helper';
import { htmlEncode } from '../../../utils/escape.utils';
import { Rights } from '@nirvana/shared/constants';
import { CompanySelectorEvents } from '../../../companySelector/types/constants';
import { GzSideMenuWebComponent } from '../../../ui/gzSideMenu';

export class ConfigurationProfilesController extends BaseWebComponentController {
  public component: ConfigurationProfilesWebComponent;
  public dataRepository: ConfigurationProfilesRepository;
  public view: ConfigurationProfilesView;
  public viewConstructor: typeof ConfigurationProfilesView;
  public dataRepositoryConstructor: typeof ConfigurationProfilesRepository;

  constructor(component: ConfigurationProfilesWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: ConfigurationProfilesView,
      viewTemplate: templateConfigurationProfiles,
      dataRepositoryConstructor: ConfigurationProfilesRepository,
    });
  }

  async getDataForView(): Promise<ConfigurationProfilesWebComponentSchema> {
    return {
      availableCompanies: await this.dataRepository.getDataForView(),
      selectedTarget: this.dependencies.getActiveCompany(),
    };
  }

  computeEventListeners() {
    return [
      {
        element: this.view.dom.$companySelectorDropdown,
        event: CompanySelectorEvents.SELECT_COMPANY,
        callback: this.onSelectCompany,
      },
      {
        element: this.view.dom.$wrapper,
        event: GlobalExclusionsEvents.DELETE_EXCLUSION_LIST,
        callback: this.onDeleteExclusionList,
      },
      {
        element: this.view.dom.$wrapper,
        event: GlobalExclusionsEvents.ASSIGN_LIST_TO_POLICIES,
        callback: this.onAssignListToPolicies,
      },
      {
        element: this.view.dom.$backButton,
        event: 'click',
        callback: this.backButtonClick,
      },
    ];
  }

  private onSelectCompany(event: WebComponentEvent) {
    Application.Main.setSelectedCompany(event.detail.currentTarget.companyId, event.detail.currentTarget.companyName)
    this.view.dom.$globalExclusionsApp?.setAttribute("data-company-id", event.detail.currentTarget.companyId);

    if (this.dependencies.getValidMaintenanceWindowTypes().length > 0 && Application.Main.hasAllRights([Rights.MANAGE_POLICIES_READ])) {
      this.view.dom.$maintenanceWindowsApp?.setAttribute("data-company-id", event.detail.currentTarget.companyId);
    }

    this.view.dom.$webAccessControlSchedulerApp?.setAttribute("data-company-id", event.detail.currentTarget.companyId);
    this.view.dom.$cloudStagingListApp?.setAttribute("data-company-id", event.detail.currentTarget.companyId);
    this.view.dom.$wacPagesApp?.setAttribute("data-company-id", event.detail.currentTarget.companyId);
  }

  private onDeleteExclusionList(event: WebComponentEvent) {
    const listId = event.detail.list;
    const listName = event.detail.listName;
    const policies = event.detail.policies;
    this.view.showExclusionListDeleteConfirmationDialog(listId, policies, this.onConfirmDeleteExclusionList.bind(this), listName);
  }

  private async onConfirmDeleteExclusionList(event: WebComponentEvent) {
    const records = [{ _id: event.detail.listId, companyId: this.dependencies.getActiveCompany().companyId }];
    const response = await this.dataRepository.removeRecords(records);

    if (!response || !response.success) {
      showErrorToast(translateFn("An unknown error has occurred."));
      return;
    }

    (this.component.shadowRoot?.querySelector('gz-side-menu') as GzSideMenuWebComponent).closeSideMenu();
    this.view.dom.$globalExclusionsApp.activeExclusionList = {label: '', value: ''};
    showSuccessToast(translateFn('Exclusion list deleted successfully.'));
  }

  private backButtonClick() {
    this.view.dom.$backButton.classList.remove('hidden');
    Application.Main.backButtonHandler(NavigationConstants.CONFIGURATION_PROFILES.NAME);
  }

  public toggleBackButton(showBackButton: boolean) {
    this.view.dom.$backButton.classList.toggle('hidden', !showBackButton);
  }

  private onAssignListToPolicies(event: WebComponentEvent) {
    const listId = event.detail.list;
    const listName = event.detail.listName;
    const assignedPoliciesIds = event.detail.inPolicies.map(item => {
      return item.id;
    });

    this.showAssignListToPoliciesConfirmationDialog(listId, assignedPoliciesIds, this.onConfirmAssignListToPolicies.bind(this), listName);
  }

  private showAssignListToPoliciesConfirmationDialog(listId: string, assignedPoliciesIds: Array<string>, confirmCallback, listName: string) {
    const editPolicyAssignmentPanel = new EditPolicyAssignmentPanelWebComponent(this.dependencies);
    editPolicyAssignmentPanel.policiesSelectorLabel = translateFn('You can assign the exclusion list {LIST_NAME} only to policies that you have permission to edit and are not inherited.', {
      LIST_NAME: `<b>${htmlEncode(listName)}</b>`,
    });
    editPolicyAssignmentPanel.previewMessage = translateFn('These policies already have the exclusion list {LIST_NAME} assigned. Some of these policies may be read-only.', {
      LIST_NAME: `<b>${htmlEncode(listName)}</b>`,
    });
    editPolicyAssignmentPanel.panelTitle = translateFn("Edit exclusion list assignment");
    editPolicyAssignmentPanel.policiesSelectorProps = {
      moduleAndSection: { moduleId: "antimalware", sectionId: "settings" },
      excludeInheritedPolicies: true,
      selectedPolicies: [...assignedPoliciesIds],
    };
    editPolicyAssignmentPanel.addEventListener(EditPolicyAssignmentEvents.CLICK_ON_SAVE, (e: CustomEvent) => {
      confirmCallback({
        listId: listId,
        listName: listName,
        policyIds: e.detail.selectedPolicies,
      });
    });

    editPolicyAssignmentPanel.displayPanel(this.view.dom.$wrapper);
  }

  private async onConfirmAssignListToPolicies(data) {
    const { listId, policyIds, listName } = data;
    const companyId = this.dependencies.getActiveCompany().companyId;
    const response = await this.dataRepository.assignListToPolicies(companyId, listId, policyIds);

    if (!response || !response.success) {
      showErrorToast(translateFn('Failed to add {scheduleName} to policies. Please try again.', {scheduleName: listName}));
      return;
    }
    showSuccessToast(translateFn('Successfully added {scheduleName} to policies.', {scheduleName: listName}));
    this.view.dom.$globalExclusionsApp.dispatchEvent(new Event(GlobalExclusionsEvents.ASSIGN_LIST_TO_POLICIES));
    this.closeDetailsPanel();
  }

  private closeDetailsPanel() {
    this.view.dom.$detailsPanel.closeSideMenu();
  }

}
